import { useFormContext } from "react-hook-form";

import { AvailableIntegrationFragment, OperationModes } from "@/apollo/types";
import { Alert, AlertIcon } from "@/components/elements/Alert";
import { formatModelName } from "@/features/models";
import cn from "@/helpers/classNames";
import { ComponentProps } from "@tw-classed/react";

import { FormValues } from "../../form";
import { EndpointBadge } from "./EndpointBadge";

type OperationModeExplainerProps = {
  sourceIntegration: AvailableIntegrationFragment;
  destinationIntegration: AvailableIntegrationFragment;
};

export const OperationModeExplainer = ({
  sourceIntegration,
  destinationIntegration,
  ...alertProps
}: OperationModeExplainerProps & ComponentProps<typeof Alert>) => {
  const { watch, setFocus } = useFormContext<FormValues>();
  const sourceModel = watch("model");
  const objectType = watch("objectTypeOption");
  const operation = watch("operation");
  const primaryMapping = watch("primaryMapping");

  const { operationMode, primaryKeyRequired } = operation;

  const handleClickSource = () => {
    setFocus("primaryMapping.sourceProperty");
  };

  const handleClickDestination = () => {
    setFocus("primaryMapping.destinationProperty");
  };

  function renderExplanation(
    sourceIntegration: AvailableIntegrationFragment,
    destinationIntegration: AvailableIntegrationFragment,
  ) {
    if (primaryKeyRequired) {
      if (operationMode === OperationModes.Update) {
        return (
          <>
            When{" "}
            <EndpointBadge
              integrationId={sourceIntegration.id}
              onClick={handleClickSource}
            >
              {primaryMapping?.sourceProperty ? (
                primaryMapping?.sourceProperty.name
              ) : (
                <em className="font-normal">select a field</em>
              )}
            </EndpointBadge>{" "}
            and{" "}
            <EndpointBadge
              integrationId={destinationIntegration.id}
              onClick={handleClickDestination}
            >
              {primaryMapping?.destinationProperty ? (
                primaryMapping?.destinationProperty.value
              ) : (
                <em className="font-normal">select a field</em>
              )}
            </EndpointBadge>{" "}
            are the same, the record will be updated in{" "}
            {destinationIntegration.name}
          </>
        );
      }

      if (operationMode === OperationModes.Upsert) {
        return (
          <>
            When{" "}
            <EndpointBadge
              integrationId={sourceIntegration.id}
              onClick={handleClickSource}
            >
              {primaryMapping?.sourceProperty ? (
                primaryMapping?.sourceProperty.name
              ) : (
                <em className="font-normal">select a field</em>
              )}
            </EndpointBadge>{" "}
            and{" "}
            <EndpointBadge
              integrationId={destinationIntegration.id}
              onClick={handleClickDestination}
            >
              {primaryMapping?.destinationProperty ? (
                primaryMapping?.destinationProperty.value
              ) : (
                <em className="font-normal">select a field</em>
              )}
            </EndpointBadge>{" "}
            are the same, the record will be updated in{" "}
            {destinationIntegration.name}. If no match is found, a new record
            will be created.
          </>
        );
      }
    }

    if (operationMode === OperationModes.Insert) {
      return (
        <>
          A new record will be created in{" "}
          <EndpointBadge integrationId={destinationIntegration.id}>
            {objectType?.objectName}
          </EndpointBadge>{" "}
          for each record in{" "}
          <EndpointBadge integrationId={sourceIntegration.id}>
            {sourceModel && formatModelName(sourceModel)}
          </EndpointBadge>
        </>
      );
    }

    if (operationMode === OperationModes.Mirror) {
      return (
        <>
          All records in{" "}
          <EndpointBadge integrationId={sourceIntegration.id}>
            {sourceModel && formatModelName(sourceModel)}
          </EndpointBadge>{" "}
          will be synced to{" "}
          <EndpointBadge integrationId={destinationIntegration.id}>
            {objectType?.objectName}
          </EndpointBadge>
        </>
      );
    }
    return null;
  }

  const explanation = renderExplanation(
    sourceIntegration,
    destinationIntegration,
  );
  if (!explanation) {
    return null;
  }

  return (
    <Alert
      status="neutral"
      {...alertProps}
      className={cn("items-start rounded", alertProps.className)}
    >
      <AlertIcon />
      <div className="leading-5">{explanation}</div>
    </Alert>
  );
};
